import React from 'react';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

import { useConfigInfoService } from '../helpers/ConfigInfo';
import { CONNECTION_STATUS_CHECK_REQUESTED } from '../helpers/connectionStatusEvents';

type ConnectionState = 'online' | 'offline' | 'backend-unreachable';

const PING_TIMEOUT_MS = 5000;
const FAST_RETRY_WINDOW_MS = 60000;
const SLOW_RETRY_WINDOW_MS = 10 * 60000;
const FAST_RETRY_DELAY_MS = 10000;
const SLOW_RETRY_DELAY_MS = 60000;
const BACKGROUND_RETRY_DELAY_MS = 10 * 60000;
const API_FAILURE_PING_THROTTLE_MS = 5000;

const getBrowserIsOnline = () =>
  typeof navigator === 'undefined' ? true : navigator.onLine !== false;

const getInfoUrl = (apiServer: string) => `${apiServer.replace(/\/$/, '')}/info`;

const getRetryDelay = (firstFailureAt: number) => {
  const elapsed = Date.now() - firstFailureAt;

  if (elapsed < FAST_RETRY_WINDOW_MS) {
    return FAST_RETRY_DELAY_MS;
  }

  if (elapsed < SLOW_RETRY_WINDOW_MS) {
    return SLOW_RETRY_DELAY_MS;
  }

  return BACKGROUND_RETRY_DELAY_MS;
};

const ConnectionStatusBanner: React.FC = () => {
  const configInfoService = useConfigInfoService();
  const apiServer = configInfoService?.apiServer;
  const [connectionState, setConnectionState] = React.useState<ConnectionState>(() =>
    getBrowserIsOnline() ? 'online' : 'offline'
  );
  const pingIdRef = React.useRef(0);
  const activeControllerRef = React.useRef<AbortController | null>(null);
  const firstBackendFailureAtRef = React.useRef<number | null>(null);
  const retryTimerRef = React.useRef<number | null>(null);
  const pingInFlightRef = React.useRef(false);
  const pingBackendRef = React.useRef<() => Promise<void>>();
  const lastApiFailurePingAtRef = React.useRef(-API_FAILURE_PING_THROTTLE_MS);

  const clearRetryTimer = React.useCallback(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const scheduleRetry = React.useCallback(() => {
    clearRetryTimer();

    if (!apiServer || !getBrowserIsOnline()) {
      return;
    }

    const firstFailureAt = firstBackendFailureAtRef.current ?? Date.now();
    firstBackendFailureAtRef.current = firstFailureAt;

    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      void pingBackendRef.current?.();
    }, getRetryDelay(firstFailureAt));
  }, [apiServer, clearRetryTimer]);

  const pingBackend = React.useCallback(async () => {
    if (!apiServer) {
      return;
    }

    if (!getBrowserIsOnline()) {
      activeControllerRef.current?.abort();
      clearRetryTimer();
      setConnectionState('offline');
      return;
    }

    if (pingInFlightRef.current) {
      return;
    }

    const pingId = pingIdRef.current + 1;
    pingIdRef.current = pingId;
    pingInFlightRef.current = true;
    clearRetryTimer();

    const controller = new AbortController();
    activeControllerRef.current = controller;
    const timeoutId = window.setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

    try {
      const response = await fetch(getInfoUrl(apiServer), {
        cache: 'no-store',
        method: 'GET',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Backend info check failed with ${response.status}`);
      }

      if (pingIdRef.current === pingId && getBrowserIsOnline()) {
        firstBackendFailureAtRef.current = null;
        setConnectionState('online');
      }
    } catch {
      if (pingIdRef.current === pingId && getBrowserIsOnline()) {
        firstBackendFailureAtRef.current = firstBackendFailureAtRef.current ?? Date.now();
        setConnectionState('backend-unreachable');
        scheduleRetry();
      }
    } finally {
      window.clearTimeout(timeoutId);
      pingInFlightRef.current = false;
      if (activeControllerRef.current === controller) {
        activeControllerRef.current = null;
      }
    }
  }, [apiServer, clearRetryTimer, scheduleRetry]);

  React.useEffect(() => {
    pingBackendRef.current = pingBackend;
  }, [pingBackend]);

  React.useEffect(() => {
    const handleOffline = () => {
      activeControllerRef.current?.abort();
      clearRetryTimer();
      setConnectionState('offline');
    };

    const handleOnline = () => {
      setConnectionState('online');
      void pingBackend();
    };

    const handleConnectionStatusCheckRequested = () => {
      if (!getBrowserIsOnline()) {
        setConnectionState('offline');
        return;
      }

      const now = Date.now();
      if (now - lastApiFailurePingAtRef.current < API_FAILURE_PING_THROTTLE_MS) {
        return;
      }

      lastApiFailurePingAtRef.current = now;
      void pingBackend();
    };

    // REQ-046: browser state, API failures, and degraded retries all feed one global notice.
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener(CONNECTION_STATUS_CHECK_REQUESTED, handleConnectionStatusCheckRequested);

    if (!getBrowserIsOnline()) {
      setConnectionState('offline');
    } else if (apiServer) {
      void pingBackend();
    }

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener(CONNECTION_STATUS_CHECK_REQUESTED, handleConnectionStatusCheckRequested);
      clearRetryTimer();
      activeControllerRef.current?.abort();
    };
  }, [apiServer, clearRetryTimer, pingBackend]);

  const message =
    connectionState === 'offline'
      ? "You're offline. Check your internet connection."
      : "We're having trouble connecting. Some features may not update.";

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={connectionState !== 'online'}
    >
      <Alert
        aria-live="polite"
        role="alert"
        severity={connectionState === 'offline' ? 'warning' : 'error'}
        sx={{ width: '100%' }}
        variant="filled"
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default ConnectionStatusBanner;
